import { API_URL } from '../config';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { io, Socket } from 'socket.io-client';
import { api } from '../api/api';

interface RespostaPendente {
  id: number;
  disparoId: number;
  nomeDisparo?: string | null;
  phone: string;
  sessionId: number;
  sessaoNome?: string | null;
  sessaoNumero?: string | null;
  disparoCriadoEm?: string | null;
  usuarioId?: number | null;
  usuarioNome?: string | null;
  usuarioFoto?: string | null;
  mensagemDisparada?: string | null;
  textoResposta: string;
  tipoResposta: string;
  receivedAt: string;
}

interface RespostaContato extends RespostaPendente {
  totalMensagens: number;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

function fmtPhone(phone: string) {
  return phone.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
}

export default function LeadsPendentesPage() {
  const [respostas, setRespostas] = useState<RespostaPendente[]>([]);
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [popupMessage, setPopupMessage] = useState<string | null>(null);
  const [confirmDeleteContact, setConfirmDeleteContact] = useState<RespostaContato | null>(null);
  const [detalhes, setDetalhes] = useState<{ resposta: RespostaPendente; loading: boolean } | null>(null);

  const loadRespostas = useCallback(async () => {
    try {
      const { data } = await api.get<RespostaPendente[]>('/disparos/respostas/pendentes');
      setRespostas(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadRespostas();
    const socket: Socket = io(API_URL, { transports: ['websocket'] });
    socket.on('dispatch:reply', (data: { textoResposta?: string }) => {
      if (data.textoResposta) loadRespostas();
    });
    return () => {
      socket.disconnect();
    };
  }, [loadRespostas]);

  const openDetalhes = (r: RespostaPendente) => {
    setDetalhes({ resposta: r, loading: false });
  };

  const mensagemDisparada = (detalhes?.resposta.mensagemDisparada || '').trim();

  const contatos = Array.from(
    respostas.reduce((map, resposta) => {
      const existing = map.get(resposta.phone);
      if (!existing) {
        map.set(resposta.phone, { ...resposta, totalMensagens: 1 });
        return map;
      }

      existing.totalMensagens += 1;
      if (new Date(resposta.receivedAt).getTime() > new Date(existing.receivedAt).getTime()) {
        map.set(resposta.phone, { ...resposta, totalMensagens: existing.totalMensagens });
      }
      return map;
    }, new Map<string, RespostaContato>())
  .values());

  const filtered = contatos.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.phone.toLowerCase().includes(q) ||
      r.textoResposta.toLowerCase().includes(q) ||
      (r.usuarioNome || '').toLowerCase().includes(q)
    );
  });

  const handleCopyPhone = async (phone: string) => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopiedPhone(phone);
      setTimeout(() => setCopiedPhone((current) => (current === phone ? null : current)), 1500);
    } catch {
      setPopupMessage('Não foi possível copiar o número.');
    }
  };

  const handleDeleteContact = async (r: RespostaContato) => {
    try {
      await api.delete(`/disparos/respostas/contato/${encodeURIComponent(r.phone)}`);
      setRespostas((prev) => prev.filter((item) => item.phone !== r.phone));
      if (detalhes?.resposta.phone === r.phone) {
        setDetalhes(null);
      }
      setConfirmDeleteContact(null);
    } catch {}
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 w-64 bg-white">
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Telefone ou mensagem..."
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>
        <span className="text-xs text-gray-400">{filtered.length} contato{filtered.length !== 1 ? 's' : ''} respondeu{filtered.length !== 1 ? 'ram' : ''}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 px-6 py-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(37, 99, 235, 0.15)' }}>
            <svg className="w-8 h-8" fill="none" stroke="#3B82F6" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <p className="text-base font-semibold text-gray-900">
            {respostas.length === 0 ? 'Nenhuma resposta recebida ainda' : 'Nenhuma resposta encontrada'}
          </p>
          <p className="text-sm text-gray-500 mt-1 max-w-sm">
            {respostas.length === 0
              ? 'Quando seus contatos responderem aos disparos, eles aparecerão aqui.'
              : 'Tente outro termo na busca.'}
          </p>
        </div>
      ) : (
      <div className="overflow-x-auto bg-white">
        <div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Contato</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Campanha</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Sessão de disparo</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Data do disparo</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Data da resposta</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Ver</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Excluir</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900">{fmtPhone(r.phone)}</span>
                        {r.totalMensagens > 1 && (
                          <span className="text-[11px] text-gray-400">{r.totalMensagens} mensagens</span>
                        )}
                      </div>
                      <button
                        type="button"
                        title={copiedPhone === r.phone ? 'Copiado!' : 'Copiar número'}
                        onClick={() => handleCopyPhone(r.phone)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
                      >
                        {copiedPhone === r.phone ? (
                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap max-w-[220px] truncate" title={r.nomeDisparo ?? undefined}>
                    {r.nomeDisparo || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {r.sessaoNome ? (
                      <div className="flex flex-col">
                        <span>{r.sessaoNome}</span>
                        {r.sessaoNumero && (
                          <span className="text-[11px] text-gray-400">{fmtPhone(r.sessaoNumero)}</span>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-center">
                    {r.disparoCriadoEm ? fmtDate(r.disparoCriadoEm) : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-center">{fmtDate(r.receivedAt)}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      title="Ver resposta do cliente"
                      onClick={() => openDetalhes(r)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      title="Excluir contato da lista"
                      onClick={() => setConfirmDeleteContact(r)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 hover:bg-[rgba(239,68,68,0.2)] text-red-500 transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {detalhes && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
              <div className="min-w-0">
                <p className="text-base font-bold text-gray-900">{fmtPhone(detalhes.resposta.phone)}</p>
                {detalhes.resposta.nomeDisparo && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate" title={detalhes.resposta.nomeDisparo}>
                    Campanha: <span className="font-semibold text-gray-700">{detalhes.resposta.nomeDisparo}</span>
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">
                  Respondido em {fmtDate(detalhes.resposta.receivedAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetalhes(null)}
                className="ml-3 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="Fechar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Mensagem disparada */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                  Mensagem disparada
                </p>
                <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg">
                  {detalhes.loading ? (
                    <p className="text-sm text-gray-400 italic">Carregando...</p>
                  ) : mensagemDisparada ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{mensagemDisparada}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Mensagem original indisponível.</p>
                  )}
                </div>
              </div>

              {/* Resposta do cliente */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-1.5">
                  Resposta do cliente
                </p>
                <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-lg">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{detalhes.resposta.textoResposta}</p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {popupMessage && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl shadow-2xl border overflow-hidden bg-white border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Aviso</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{popupMessage}</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setPopupMessage(null)}
                className="px-5 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-all"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {confirmDeleteContact && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl shadow-2xl border overflow-hidden bg-white border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Confirmar exclusao</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                Tem certeza que deseja excluir este contato?
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap mt-4">
                Todas as mensagens do numero {fmtPhone(confirmDeleteContact.phone)} serao apagadas do painel.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeleteContact(null)}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteContact(confirmDeleteContact)}
                className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-all"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
